/**
 * Google Identity Services token kliens.
 *
 * Frontend-only OAuth: nincs client secret, nincs refresh token. Az access token
 * kizárólag a memóriában él (nem localStorage, nem cookie), élettartama ~60 perc.
 * Lejáratkor előbb csendes megújítást próbálunk, és csak akkor kérünk felhasználói
 * gesztust, ha az nem megy.
 */
import { CONFIG } from '../config';

interface TokenResponse {
  access_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

interface TokenClient {
  callback: (resp: TokenResponse) => void;
  requestAccessToken(opts?: { prompt?: string; hint?: string }): void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient(cfg: {
            client_id: string;
            scope: string;
            hint?: string;
            hd?: string;
            callback: (resp: TokenResponse) => void;
            error_callback?: (err: { type?: string }) => void;
          }): TokenClient;
          revoke(token: string, done?: () => void): void;
        };
      };
    };
  }
}

export class AuthError extends Error {
  constructor(message: string, readonly needsInteraction = false) {
    super(message);
    this.name = 'AuthError';
  }
}

let client: TokenClient | null = null;
let token: string | null = null;
let expiresAt = 0;
let userHint: string | undefined;
let pending: Promise<string> | null = null;

/** Megvárja, míg a GIS script betöltődik (async script tag az index.html-ben). */
async function waitForGis(timeoutMs = 10_000): Promise<void> {
  const started = Date.now();
  while (!window.google?.accounts?.oauth2) {
    if (Date.now() - started > timeoutMs) {
      throw new AuthError('A Google bejelentkezési szolgáltatás nem érhető el.');
    }
    await new Promise((r) => setTimeout(r, 50));
  }
}

/** A Drive state-ből érkező userId-t átadjuk hintként, hogy a jó fiókra essen. */
export function setUserHint(hint?: string): void {
  userHint = hint;
}

async function ensureClient(): Promise<TokenClient> {
  if (client) return client;
  await waitForGis();
  if (!CONFIG.clientId) {
    throw new AuthError(
      'Nincs beállítva a Google Client ID. A szerveren írd be a config.js fájl ' +
        '"clientId" mezőjébe az OAuth kliens azonosítót.',
    );
  }
  client = window.google!.accounts.oauth2.initTokenClient({
    client_id: CONFIG.clientId,
    scope: CONFIG.scopes,
    hint: userHint,
    hd: CONFIG.hostedDomain,
    callback: () => {},
  });
  return client;
}

/**
 * Érvényes access tokent ad vissza.
 * @param interactive true esetén felugró ablakot is nyithat (felhasználói gesztusból hívd!)
 */
export function getToken(interactive = false): Promise<string> {
  if (token && Date.now() < expiresAt - 60_000) return Promise.resolve(token);
  if (pending) return pending;

  pending = (async () => {
    const c = await ensureClient();
    return await new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new AuthError('Időtúllépés a hitelesítés során.', true));
      }, 60_000);

      c.callback = (resp) => {
        clearTimeout(timer);
        if (resp.error || !resp.access_token) {
          reject(
            new AuthError(
              resp.error_description || resp.error || 'Sikertelen hitelesítés.',
              true,
            ),
          );
          return;
        }
        token = resp.access_token;
        expiresAt = Date.now() + (resp.expires_in ?? 3600) * 1000;
        resolve(token);
      };

      try {
        c.requestAccessToken({ prompt: interactive ? 'consent' : '', hint: userHint });
      } catch (err) {
        clearTimeout(timer);
        reject(new AuthError(String(err), true));
      }
    });
  })();

  pending.finally(() => {
    pending = null;
  });
  return pending;
}

/** Érvényteleníti a memóriában tárolt tokent (pl. 401 után). */
export function invalidateToken(): void {
  token = null;
  expiresAt = 0;
}
